import { useContext, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Icon, Typography } from '@mui/material'
import ModalDialog from '../modal-dialog/ModalDialog'
import AuthContext from '../../units/auth/AuthContext'
import PlatformContext from '../../context/platform/PlatformContext'
import { useNotificationContext } from '../../units/notifications/NotificationContext'
import { resolveUploadUrl } from '../../helpers/file.helper'
import { actionActs, actionOf } from '../../data-organizers/announcements/resolveAnnouncementActions'
import AnnouncementSendForm from './AnnouncementSendForm'

const DEFAULT_ACKNOWLEDGEMENT = 'תודה, פנייתך נקלטה.'
const CONVERSION_FAILED = 'לא הצלחנו לרשום את הפנייה. נא לפנות לתמיכה.'

export const ACTION_FAILED = 'לא הצלחנו לבצע את הפעולה. נא לפנות לתמיכה.'

function captionedBy(caption, icon) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            {caption}
            {icon}
        </Box>
    )
}

export default function AnnouncementPopup({
    workingSet = null,
    preview = false,
    onClose,
    onNavigated,
    onImageFailed,
    onConverted,
    onResolved,
}) {
    const token = useContext(AuthContext)?.token
    const platform = useContext(PlatformContext)
    const { addNotification } = useNotificationContext()
    const [current, setCurrent] = useState(workingSet?.current ?? null)
    const [imageFailed, setImageFailed] = useState(false)
    const [converting, setConverting] = useState(false)
    const [sending, setSending] = useState(null)

    // A send that is still open when the popup goes away was abandoned, and the popup is
    // the only thing that knows it: the form is modal over it, so there is no close
    // command to press, and the announcement is taken away rather than dismissed.
    const abandonPendingSend = useRef(null)

    const abandonSend = () => {
        const abandon = abandonPendingSend.current
        abandonPendingSend.current = null

        return abandon?.() ?? Promise.resolve()
    }

    useEffect(() => {
        abandonSend()
        setCurrent(workingSet?.current ?? null)
        setImageFailed(false)
        setConverting(false)
        setSending(null)
    }, [workingSet])

    useEffect(() => () => {
        abandonPendingSend.current?.()
        abandonPendingSend.current = null
    }, [])

    if (workingSet == null || current == null) {
        return null
    }

    const isAlert = current.type?.key === 'alert'

    const paragraphs = (current.bodyText ?? '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line !== '')

    const moveTo = (announcement, trigger) => {
        setImageFailed(false)
        setConverting(false)
        setSending(null)
        setCurrent(announcement)
        onNavigated?.(announcement, trigger)
    }

    const failImage = () => {
        setImageFailed(true)
        onImageFailed?.()
    }

    const conversionButtonText = (current.conversionButtonText ?? '').trim()

    const acknowledge = () => {
        addNotification({
            type: 'success',
            content: (current.conversionMessage ?? '').trim() || DEFAULT_ACKNOWLEDGEMENT,
        })
    }

    const report = (content) => addNotification({ type: 'error', content })

    const record = (source, outcome) => onConverted?.(current, { source, outcome })

    const resolve = (conversion, outcome) => {
        if (conversion?.documentId == null) {
            return Promise.resolve()
        }

        return onResolved?.(current, conversion.documentId, outcome) ?? Promise.resolve()
    }

    // The acknowledgement follows the action: an action that never happened is never
    // acknowledged, and a press in preview is acknowledged without anything happening.
    const trigger = async (source) => {
        if (preview) {
            acknowledge()
            return
        }

        const action = actionOf(current, source)

        if (action?.kind === 'email') {
            await openSendForm(source)
            return
        }

        setConverting(true)

        try {
            if (action?.kind === 'url' && platform?.openExternalUrl?.(action.url) !== true) {
                report(ACTION_FAILED)
                return
            }

            await record(source, 'success')
            acknowledge()
        }
        catch {
            report(CONVERSION_FAILED)
        }
        finally {
            setConverting(false)
        }
    }

    const openSendForm = async (source) => {
        setConverting(true)

        try {
            const conversion = await record(source, 'pending')

            abandonPendingSend.current = () => resolve(conversion, 'abandoned')
            setSending({ conversion })
        }
        catch {
            report(CONVERSION_FAILED)
        }
        finally {
            setConverting(false)
        }
    }

    const cancelSend = () => {
        setSending(null)
        abandonSend()
    }

    const completeSend = async (deliver) => {
        const sent = sending
        abandonPendingSend.current = null
        setConverting(true)

        try {
            await deliver()
            setSending(null)
            await resolve(sent?.conversion, 'success')
            acknowledge()
        }
        catch {
            setSending(null)
            await resolve(sent?.conversion, 'error')
            report(ACTION_FAILED)
        }
        finally {
            setConverting(false)
        }
    }

    const closePopup = () => {
        setSending(null)
        abandonSend()
        onClose?.()
    }

    const navigationCommands = [
        {
            label: 'הקודם',
            color: 'secondary',
            disabled: converting,
            onClick: () => moveTo(workingSet.previous(), 'previous'),
        },
        {
            label: 'הבא',
            color: 'secondary',
            disabled: converting,
            onClick: () => moveTo(workingSet.next(), 'next'),
        },
    ]

    const closeCommand = {
        label: captionedBy('סגירה', <Icon fontSize="small">close</Icon>),
        color: 'secondary',
        disabled: converting,
        onClick: closePopup,
    }

    const conversionIcon = (
        <Box
            component="span"
            data-testid="announcement-conversion-icon"
            sx={{
                width: 20,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {converting
                ? (
                    <CircularProgress
                        data-testid="announcement-conversion-progress"
                        size={16}
                        color="inherit"
                    />
                )
                : <Icon fontSize="small">favorite</Icon>}
        </Box>
    )

    const conversionCommand = {
        label: captionedBy(conversionButtonText, conversionIcon),
        color: 'primary',
        disabled: converting,
        onClick: () => trigger('button'),
    }

    const commands = [
        ...(workingSet.canNavigate ? navigationCommands : []),
        closeCommand,
        ...(conversionButtonText === '' ? [] : [conversionCommand]),
    ]

    const title = (
        <>
            {isAlert && (
                <Icon
                    data-testid="announcement-alert"
                    color="error"
                    sx={{ mr: 1 }}
                >
                    error
                </Icon>
            )}
            {current.title}
        </>
    )

    const imageActs = actionActs(actionOf(current, 'image'))

    return (
        <>
            <ModalDialog
                open
                title={title}
                titleComponent="h1"
                commands={commands}
                onClose={closePopup}
            >
                <Box component="article">
                    {paragraphs.map((paragraph, index) => (
                        <Typography key={index} variant="body2" gutterBottom>
                            {paragraph}
                        </Typography>
                    ))}

                    {current.image?.url && !imageFailed && (
                        <Box
                            component="img"
                            src={resolveUploadUrl(current.image.url, token)}
                            alt={current.title}
                            onError={failImage}
                            onClick={imageActs ? () => trigger('image') : undefined}
                            sx={{
                                width: '100%',
                                mb: 2,
                                cursor: imageActs ? 'pointer' : 'default',
                            }}
                        />
                    )}

                    {imageFailed && (
                        <Box
                            data-testid="announcement-image-placeholder"
                            sx={{
                                width: '100%',
                                py: 4,
                                mb: 2,
                                textAlign: 'center',
                                backgroundColor: 'grey.200',
                            }}
                        >
                            <Icon>image_not_supported</Icon>
                        </Box>
                    )}
                </Box>
            </ModalDialog>

            {sending != null && (
                <AnnouncementSendForm
                    announcement={current}
                    busy={converting}
                    onSend={completeSend}
                    onClose={cancelSend}
                />
            )}
        </>
    )
}
